'use client';

import useTranslation from 'next-translate/useTranslation';
import { Breadcrumbs, Container, Heading, PageContent } from '@stereoagency/ui';
import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import { ReactNode, useContext, useEffect, useRef } from 'react';
import flush from 'just-flush';
import ResearchProjectRow from '../GroupOrPlatformProjectRow.client';
import ResearchGroupDiseaseAreas from '../GroupOrPlatformDiseases.client';
import ResearchGroupPageNews from '../GroupOrPlatformNewsGrid.client';
import {
  researchGroupPageQuery,
  researchGroupRelatedArticlesQuery,
  researchGroupRelatedJobsQuery,
  researchGroupRelatedProjectsQuery
} from './researchGroupPageQuery';
import ResearchGroupPageHero from './ResearchGroupPageHero.client';
import ResearchGroupPageSidebar from '../GroupOrPlatformSidebar.client';
import ResearchGroupPagePubmed from '../GroupOrPlatformPubmedListing.client';
import ResearchGroupPageJobs from '../GroupOrPlatformJobs.client';
import { SimpleBlockContent } from '@/components/sanity/client';
import { createDocumentURL, findInKeyArray } from '@/utils';
import useActiveSectionIndex from '@/hooks/useActiveSectionIndex';
import { SpecialPagesContext } from '@/components/context';
import { PagePatternContext } from '@/components/layout/PagePattern';
import { diseasesForResearchGroupQuery } from './diseasesForResearchGroupQuery';
import GroupOrPlatformDepartmentsSection, {
  SingleProfile
} from '../GroupOrPlatformDepartmentsSection.client';

type Props = {
  lang: string;
  researchGroup: (typeof researchGroupPageQuery)[1];
  diseases?: (typeof diseasesForResearchGroupQuery)[1];
  projects?: (typeof researchGroupRelatedProjectsQuery)[1]['projects'];
  articles?: (typeof researchGroupRelatedArticlesQuery)[1]['articles'];
  jobs?: (typeof researchGroupRelatedJobsQuery)[1]['jobs'];
  researchGroupLeaderProfile?: SingleProfile;
  departmentProfiles?: SingleProfile[];
  aboutContent?: ReactNode;
  researchContent?: ReactNode;
  bioContent?: ReactNode;
  teamContent?: ReactNode;
};

export default function ResearchGroupPage({
  lang,
  researchGroup,
  projects,
  articles,
  jobs,
  diseases,
  researchGroupLeaderProfile,
  departmentProfiles,
  aboutContent,
  researchContent,
  bioContent,
  teamContent
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeId = useActiveSectionIndex(containerRef);
  const { set: setPattern } = useContext(PagePatternContext);
  const { pages } = useContext(SpecialPagesContext);
  const { t } = useTranslation();

  const title = researchGroup.title
    ? findInKeyArray(researchGroup.title, [lang, defaultLanguage.id])
    : null;

  const introduction = researchGroup.introduction
    ? findInKeyArray(researchGroup.introduction, [lang, defaultLanguage.id])
    : null;

  const diseaseAreas =
    researchGroup.taxonomies?.filter(
      (tax) => tax.type === TaxonomyType.DISEASE_AREA
    ) ?? [];

  const departments = researchGroup.departments
    ? findInKeyArray(researchGroup.departments, [lang, defaultLanguage.id])
    : null;

  const hasResearchContent = Boolean(
    researchContent || diseaseAreas.length || projects?.length
  );
  const hasBioContent = !!bioContent;
  const hasTeamContent = Boolean(!!departments?.value?.length || teamContent);
  const hasNews = !!articles?.length;
  const hasJobs = !!jobs?.length;

  const breadcrumbActions = flush([
    pages.research_group_listing_page?.href
      ? {
          href: pages.research_group_listing_page.href,
          label: pages.research_group_listing_page.title
        }
      : null,
    researchGroup?.slug?.current
      ? {
          href: createDocumentURL(
            lang,
            'researchGroup',
            researchGroup.slug.current
          ),
          label: title?.value ?? ''
        }
      : null
  ]);

  const departmentsInfo = departments?.value ?? [];

  useEffect(() => {
    setPattern('3');
  }, []);

  return (
    <>
      <Container>
        <Breadcrumbs actions={breadcrumbActions} />
      </Container>

      <ResearchGroupPageHero
        lang={lang}
        researchGroup={researchGroup}
        researchGroupLeaderProfile={researchGroupLeaderProfile}
      />

      <Container ref={containerRef} className="[&_h2]:text-link-fg-base">
        <div className="grid gap-6 grid-cols-1 grid-flow-row auto-rows-max md:grid-cols-[18rem_1fr] md:gap-10 lg:gap-12 lg:grid-cols-[22rem_1fr]">
          <div className="grid grid-cols-1 grid-rows-1 items-start">
            {!!researchGroupLeaderProfile?._id && (
              <ResearchGroupPageSidebar
                activeId={activeId}
                profile={researchGroupLeaderProfile}
                hasPublications={
                  !!researchGroupLeaderProfile?.pubmedArticles?.length
                }
                hasResearchContent={hasResearchContent}
                hasTeamContent={hasTeamContent}
                hasNews={hasNews}
                hasJobs={hasJobs}
                accentColor="--colors-yellow-700"
              />
            )}
          </div>
          <PageContent className="my-0 md:my-0 md:col-start-2">
            {!!introduction?.value && (
              <div id="about">
                <Heading
                  as="h3"
                  variant="h3"
                  className="font-body font-normal md:text-heading-h3-desktop-s"
                >
                  <SimpleBlockContent value={introduction.value} />
                </Heading>
              </div>
            )}

            {aboutContent}

            {hasResearchContent && (
              <>
                {' '}
                <Heading id="research" as="h2" variant="h2">
                  {t('research-group-or-platform:sections.research.title')}
                </Heading>
                {researchContent}
                {!!projects?.length && (
                  <div>
                    <Heading as="h3" variant="h3" className="mb-6">
                      {t(
                        'research-group-or-platform:sections.research.projects'
                      )}
                    </Heading>
                    <div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-2">
                      {projects.map((project) => (
                        <ResearchProjectRow
                          key={project._id}
                          project={project}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {!!diseaseAreas?.length && (
                  <div>
                    <Heading as="h3" variant="h3" className="mb-6">
                      {t(
                        'research-group-or-platform:sections.research.diseases'
                      )}
                    </Heading>
                    <ResearchGroupDiseaseAreas diseases={diseases ?? []} />
                  </div>
                )}
              </>
            )}

            {hasBioContent && (
              <>
                <Heading id="bio" as="h2" variant="h2">
                  {t('research-group-or-platform:sections.bio.title')}
                </Heading>

                {bioContent}
              </>
            )}

            {hasTeamContent && (
              <>
                <Heading id="team" as="h2" variant="h2">
                  {t('research-group-or-platform:sections.team.title')}
                </Heading>

                <GroupOrPlatformDepartmentsSection
                  departments={departmentsInfo}
                  departmentProfiles={departmentProfiles}
                  leaderProfile={researchGroupLeaderProfile}
                />

                {teamContent}
              </>
            )}

            <ResearchGroupPagePubmed
              query={researchGroupLeaderProfile?.pubmedQuery}
              articles={researchGroupLeaderProfile?.pubmedArticles}
            />

            <ResearchGroupPageNews articles={articles ?? []} />

            <ResearchGroupPageJobs jobs={jobs ?? []} />
          </PageContent>
        </div>
      </Container>
    </>
  );
}
